import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { ArrowLeft, Mail, Hash, Building2, MessageSquare, Sparkles } from 'lucide-react';
import { DANA, openDana } from '@/lib/dana';
import { DanaAvatar } from '@/features/shared/components/branding/DanaAvatar';

/** Read-only profile for Dana, the AI HR Assistant — shown when the directory /
 *  org chart links to her virtual record. No backend fetch (she isn't a real
 *  employee); everything renders from the brand-locked identity. */
export const DanaProfile: React.FC<{ backTo: string }> = ({ backTo }) => {
  const navigate = useNavigate();

  const capabilities = [
    'Answer HR questions — leave, payslips, policies, documents',
    'Help you find people and navigate KaziPeople',
    'Welcome new joiners and celebrate birthdays & work anniversaries',
    'Send weekly digests to keep your team in the loop',
  ];

  return (
    <div className="space-y-4">
      <Button variant="ghost" onClick={() => navigate(backTo)} className="text-orange-700">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back
      </Button>

      {/* Header */}
      <Card className="border-2 border-orange-200 dark:border-orange-500/30 overflow-hidden">
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-6 text-white">
          <div className="flex items-center gap-4 flex-wrap">
            <DanaAvatar size={64} ring />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-2xl font-bold">{DANA.name}</h2>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-white/20 rounded-full px-2 py-0.5">
                  <Sparkles className="w-3 h-3" /> AI Assistant
                </span>
              </div>
              <p className="text-white/90">{DANA.position}</p>
              <p className="text-white/75 text-sm">{DANA.department} · Always available</p>
            </div>
            <div className="ml-auto">
              <Button
                onClick={() => openDana()}
                className="bg-white text-orange-700 hover:bg-orange-50 border-0 font-semibold"
              >
                <MessageSquare className="w-4 h-4 mr-2" /> Chat with Dana
              </Button>
            </div>
          </div>
        </div>
        <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6">
          <div className="flex items-start gap-3">
            <Hash className="w-4 h-4 text-orange-500 mt-0.5" />
            <div>
              <p className="text-xs text-muted-foreground">Employee ID</p>
              <p className="text-sm text-foreground">{DANA.employeeId}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Building2 className="w-4 h-4 text-orange-500 mt-0.5" />
            <div>
              <p className="text-xs text-muted-foreground">Department</p>
              <p className="text-sm text-foreground">{DANA.department}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Mail className="w-4 h-4 text-orange-500 mt-0.5" />
            <div>
              <p className="text-xs text-muted-foreground">Email</p>
              <a href={`mailto:${DANA.email}`} className="text-sm text-orange-700 break-words hover:underline">{DANA.email}</a>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* About */}
      <Card className="border-orange-100 dark:border-orange-500/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-orange-700">
            <Sparkles className="w-5 h-5 text-orange-600" /> About Dana
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-foreground">
            Dana is your AI HR Assistant — think of her as part of the HR team. She works
            inside KaziPeople, respects your role and permissions, and is available to
            everyone, any time, in English or Swahili.
          </p>
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">What Dana can help with</p>
            <ul className="space-y-1.5">
              {capabilities.map((c) => (
                <li key={c} className="flex gap-2 text-sm text-foreground">
                  <span className="text-orange-400 select-none">•</span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Badge className="text-[10px] bg-orange-500 text-white border border-orange-500">AI-001</Badge>
            <Badge className="text-[10px] bg-success-soft text-success-strong border border-transparent">Always available</Badge>
            <Button onClick={() => openDana()} size="sm" className="ml-auto bg-orange-500 hover:bg-orange-600 text-white">
              <MessageSquare className="w-4 h-4 mr-2" /> Ask Dana something
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DanaProfile;
